import { useParams } from 'react-router-dom';
import { PieChart } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useGetQuotaStatus } from '@/reactQuery/get';
import useTranslation from '@/hooks/useTranslation';
import { QuotaOptionStatus } from '@/types';
import AnalysisSection from '@/components/analysis/AnalysisSection';

function getBarColor(percentage: number): string {
  if (percentage >= 100) return 'bg-red-500';
  if (percentage >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function QuotaOptionRow({ option }: { option: QuotaOptionStatus }) {
  const { lang } = useTranslation();
  const barColor = getBarColor(option.fill_percentage);
  const clampedFill = Math.min(option.fill_percentage, 100);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-neutral-700 truncate max-w-[200px]">{option.value}</span>
        <div className="flex items-center gap-2 text-neutral-500">
          <span>
            {option.current} / {option.target}
          </span>
          <span
            className={cn(
              'font-semibold min-w-[48px] text-right',
              option.is_full ? 'text-red-600' : 'text-neutral-900',
            )}
          >
            {option.is_full ? lang.get('msg.quotaFull') : `${Math.round(option.fill_percentage)}%`}
          </span>
        </div>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-200/60">
        <div
          className={cn('h-full rounded-full transition-all', barColor)}
          style={{ width: `${clampedFill}%` }}
        />
      </div>
    </div>
  );
}

function QuotaTrackingWidget() {
  const { lang } = useTranslation();
  const {
    organization_id: organizationId,
    workspace_id: workspaceId,
    project_id: projectId,
  } = useParams();

  const { data, isLoading } = useGetQuotaStatus(organizationId, workspaceId, projectId);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-neutral-200/80 bg-white p-5 shadow-sm space-y-4">
        <Skeleton className="h-5 w-48 rounded" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  }

  if (!data?.quotas?.length) {
    return null;
  }

  return (
    <AnalysisSection
      title={lang.get('msg.quotaTrackingTitle')}
      icon={<PieChart className="h-5 w-5" />}
      fullWidth
      defaultOpen
    >
      <div className="flex flex-col gap-4">
        {data.quotas.map((question) => (
          <div
            key={question.question_id}
            className="flex flex-col gap-3 rounded-lg border border-neutral-100 bg-neutral-50/50 p-4"
          >
            <h4 className="text-sm font-semibold text-neutral-800">{question.question_text}</h4>
            <div className="flex flex-col gap-3">
              {question.options.map((option) => (
                <QuotaOptionRow key={option.value} option={option} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </AnalysisSection>
  );
}

export default QuotaTrackingWidget;
